// Shared constants + helpers for CriLam Design Studio
// (AI Visualizer + AI Door Designer)

import jsPDF from 'jspdf';

export const FINISHES = [
  { id: 'high-gloss-white', label: 'Snow White', code: 'HG-4501', color: '#FFFFFF', border: true },
  { id: 'high-gloss-cream', label: 'Ivory Cream', code: 'HG-4512', color: '#FFF8E7' },
  { id: 'matte-grey', label: 'Storm Grey', code: 'MT-6103', color: '#6B7280' },
  { id: 'wood-walnut', label: 'Royal Walnut', code: 'WG-8802', color: '#5D4037' },
  { id: 'wood-oak', label: 'Honey Oak', code: 'WG-8815', color: '#D4A574' },
  { id: 'acrylic-champagne', label: 'Champagne Gold', code: 'AC-7701', color: '#F7E7CE' },
  { id: 'acrylic-burgundy', label: 'Merlot Burgundy', code: 'AC-7714', color: '#800020' },
  { id: 'high-gloss-black', label: 'Onyx Black', code: 'HG-4599', color: '#1a1a1a' },
];

export const TEXTURES = [
  { id: 'high-gloss', label: '6H Acrylic High-Gloss', desc: 'Mirror-like reflection · Anti-yellowing', badge: 'Signature' },
  { id: 'matte', label: 'Matte Suede', desc: 'Anti-fingerprint · Velvety touch', badge: 'Premium' },
  { id: 'woodgrain', label: 'Woodgrain Texture', desc: 'Tactile wood-feel · Authentic look', badge: 'Popular' },
  { id: 'metallic-pearl', label: 'Metallic Pearl', desc: 'Iridescent shimmer · Luxury sheen', badge: 'Luxury' },
  { id: 'solid-6h', label: '6H Solid', desc: 'Uniform tone · Satin finish', badge: 'Classic' },
];

export const STYLES = [
  { id: 'modern', label: 'Modern Minimalist' },
  { id: 'contemporary', label: 'Contemporary' },
  { id: 'traditional', label: 'Traditional Indian' },
  { id: 'luxury', label: 'Luxury Premium' },
];

export const HANDLE_PROFILES = [
  { id: 'handleless-j', label: 'Handleless J-Profile', desc: 'Recessed grip · Ultra-clean' },
  { id: 'gola', label: 'Gola Profile', desc: 'Aluminum handle strip · Modern' },
  { id: 'handle-based', label: 'Designer Handle', desc: 'Statement hardware · Classic' },
];

export const HANDLE_COLORS = [
  { id: 'gold', label: 'Champagne Gold', hex: '#C9A85A' },
  { id: 'chrome', label: 'Polished Chrome', hex: '#B8BAC0' },
  { id: 'black', label: 'Matte Black', hex: '#1a1a1a' },
  { id: 'brass', label: 'Antique Brass', hex: '#9B7B3F' },
];

export const WARDROBE_TYPES = [
  { id: 'openable', label: 'Openable Shutter', desc: 'Classic hinged doors' },
  { id: 'sliding', label: 'Sliding Doors', desc: 'Space-saving, quiet track' },
  { id: 'walk-in', label: 'Walk-in Closet', desc: 'Open shelves + island' },
];

export const WARDROBE_DOOR_DESIGNS = [
  { id: 'plain', label: 'Plain Shutter' },
  { id: 'cnc-grooved', label: 'CNC Grooved' },
  { id: 'fluted', label: 'Fluted Slat' },
  { id: 'mirror', label: 'Mirror Insert' },
  { id: 'glass', label: 'Frosted Glass' },
  { id: 'louvered', label: 'Louvered' },
];

export const LIGHTINGS = [
  { id: 'day', label: 'Bright Daylight', icon: '☀' },
  { id: 'warm-night', label: 'Warm Evening', icon: '🌙' },
  { id: 'cool-night', label: 'Cool Night LED', icon: '✦' },
];

export const DOOR_ROOMS = [
  { id: 'main-entrance', label: 'Main Entrance', desc: 'Grand statement door · 7×3 ft', badge: 'Statement' },
  { id: 'bedroom', label: 'Bedroom', desc: 'Refined interior · 6.5×2.5 ft', badge: 'Popular' },
  { id: 'bathroom', label: 'Bathroom', desc: 'Zero-swelling · Moisture-safe', badge: 'Waterproof' },
  { id: 'kitchen', label: 'Kitchen', desc: 'Heat & stain resistant · 6.5×2.5 ft', badge: 'Durable' },
  { id: 'pooja', label: 'Pooja Room', desc: 'Auspicious sacred motifs', badge: 'Traditional' },
  { id: 'utility', label: 'Utility / Balcony', desc: 'Practical · Weather-resistant', badge: 'Practical' },
];

export const DOOR_SIZES = [
  { id: '7x3', label: '7 ft × 3 ft', desc: 'Standard main door', defaultRooms: ['main-entrance'] },
  { id: '6.5x2.5', label: '6.5 ft × 2.5 ft', desc: 'Standard bedroom / kitchen / pooja', defaultRooms: ['bedroom', 'kitchen', 'pooja'] },
  { id: '6.5x2', label: '6.5 ft × 2 ft', desc: 'Standard bathroom / utility', defaultRooms: ['bathroom', 'utility'] },
  { id: 'custom', label: 'Custom Size', desc: 'Enter your own dimensions', defaultRooms: [] },
];

export const DOOR_HANDLES = [
  { id: 'gold', label: 'Champagne Gold', hex: '#C9A85A' },
  { id: 'chrome', label: 'Polished Chrome', hex: '#B8BAC0' },
  { id: 'black', label: 'Matte Black', hex: '#1a1a1a' },
  { id: 'brass', label: 'Antique Brass', hex: '#9B7B3F' },
  { id: 'none', label: 'Concealed / None', hex: '#EEEEEE' },
];

export const DOOR_FRAMES = [
  { id: 'wpc', label: 'WPC Frame', desc: 'Wood-Plastic Composite · Moisture-safe' },
  { id: 'wooden', label: 'Wooden Frame', desc: 'Solid natural wood · Classic' },
  { id: 'none', label: 'No Frame', desc: 'Seamless installation' },
];

export const CNC_CATEGORIES = [
  { id: 'geometric', label: 'Geometric' },
  { id: 'floral-traditional', label: 'Floral / Traditional' },
  { id: 'modern-minimal', label: 'Modern Minimal' },
  { id: 'panel-classic', label: 'Panel Classic' },
  { id: 'special', label: 'Special Collection' },
];

// -------- Door Material Families (client copy of catalog) --------
export const DOOR_MATERIALS = [
  {
    id: 'acrylic-laminate',
    name: 'Acrylic Laminate',
    tag: 'Signature',
    short: '6H Hardness · Mirror Finish · Bathroom-Safe',
    long: 'CriLam\'s flagship 6H Acrylic laminate — mirror-like reflection, anti-fingerprint, 100% waterproof. PUR-bonded, nail-free.',
    accentGradient: 'from-slate-100 via-white to-slate-50',
    accentBorder: 'border-slate-300',
    accentIcon: 'sparkles',
    features: ['6H Surface Hardness', 'Mirror-Like Reflection', '100% Waterproof · Bathroom-Safe', 'Anti-Yellowing · UV-Stable'],
    bestFor: ['kitchen', 'bathroom', 'bedroom', 'utility'],
    cncCompat: 'excellent',
  },
  {
    id: 'mica',
    name: 'Mica / Decorative Laminate',
    tag: 'Best Value',
    short: '200+ Shades · Brand-Compatible · Popular',
    long: 'Decorative HPL/Mica bonded to CriLam\'s PUR-bonded nail-free core. Massive shade variety, brand-agnostic.',
    accentGradient: 'from-amber-50 via-orange-50 to-yellow-50',
    accentBorder: 'border-amber-300',
    accentIcon: 'palette',
    features: ['200+ Curated Shades', 'Compatible with Merino / Greenlam / Sunmica', 'PUR-Bonded · Nail-Free', 'Scratch & Stain Resistant'],
    bestFor: ['bedroom', 'kitchen', 'utility', 'pooja'],
    cncCompat: 'excellent',
  },
  {
    id: 'veneer',
    name: 'Natural Veneer (Nail-Free)',
    tag: 'Premium',
    short: 'Real Wood · Warm Character · CriLam Signature',
    long: 'Authentic paper-thin real-wood veneer PUR-bonded to CriLam\'s proprietary nail-free core. The warmth of real wood without solid timber\'s risks.',
    accentGradient: 'from-amber-800/10 via-amber-700/5 to-amber-900/10',
    accentBorder: 'border-amber-800/40',
    accentIcon: 'tree-pine',
    features: ['10 Real-Wood Species', 'PUR-Bonded · Absolutely Nail-Free', 'Authentic Grain & Natural Character', 'Ages into Rich Patina'],
    bestFor: ['main-entrance', 'bedroom', 'pooja'],
    cncCompat: 'medium',
    cncWarning: 'Deep-groove CNC (>4mm) not advised on softer veneers.',
  },
  {
    id: 'pu-paint',
    name: 'PU Paint Finish',
    tag: 'Luxury',
    short: 'Any Custom Colour · Automotive Sheen',
    long: 'Automotive-grade Polyurethane paint over PUR-bonded nail-free core. Any colour, 3 sheens, mirror-smooth pigmented surface.',
    accentGradient: 'from-purple-50 via-pink-50 to-rose-50',
    accentBorder: 'border-purple-300',
    accentIcon: 'droplet',
    features: ['Any Custom Colour (RAL / Asian Paints)', '3 Sheens: Matte / Satin / Gloss', 'Automotive-Grade Finish', 'Applied Post-CNC — Crisp Grooves'],
    bestFor: ['main-entrance', 'bedroom', 'pooja'],
    cncCompat: 'excellent',
  },
];

// -------- Veneer Species (10) with rich metadata for cards --------
export const VENEER_SPECIES = [
  { id: 'burma-teak', name: 'Burma Teak', hex: '#B08556', origin: 'Myanmar (Burma)', grain: 'straight, uniform golden brown', story: 'India\'s most prized wood — golden warm tones that deepen with age.', cncCompat: 'excellent' },
  { id: 'royal-walnut', name: 'Royal Walnut', hex: '#5D4037', origin: 'American', grain: 'swirling dark chocolate with rich waves', story: 'Deep chocolate tones with dramatic swirls. Signature of ultra-luxury homes.', cncCompat: 'excellent' },
  { id: 'english-oak', name: 'English Oak', hex: '#B58D5E', origin: 'United Kingdom', grain: 'prominent open grain with cathedral patterns', story: 'Bold cathedral grain, honey-caramel tones. British classic.', cncCompat: 'medium' },
  { id: 'burma-mahogany', name: 'Burma Mahogany', hex: '#8B4A3E', origin: 'Central America', grain: 'fine ribbon grain with deep reddish-brown', story: 'Reddish-brown depth with silky ribbon grain. Regal presence.', cncCompat: 'excellent' },
  { id: 'indian-rosewood', name: 'Indian Rosewood', hex: '#4B2F2A', origin: 'South India', grain: 'dense dark grain with purple undertones', story: 'The wood of thrones — deepest darks with purple hints.', cncCompat: 'excellent' },
  { id: 'indian-sheesham', name: 'Indian Sheesham', hex: '#9C5A3D', origin: 'North India', grain: 'flowing rustic streaks in reddish-brown', story: 'Punjab\'s beloved rosewood cousin — warm honeyed patina.', cncCompat: 'excellent' },
  { id: 'sapele', name: 'Sapele Ribbon', hex: '#7B4028', origin: 'West Africa', grain: 'interlocking ribbon with iridescent shimmer', story: 'African cousin of mahogany with hypnotic ribbon shimmer.', cncCompat: 'medium' },
  { id: 'african-wenge', name: 'African Wenge', hex: '#3A2A22', origin: 'Central Africa', grain: 'very dark tight straight lines', story: 'Almost black with linear precision. The ultimate contemporary statement.', cncCompat: 'medium' },
  { id: 'canadian-maple', name: 'Canadian Maple', hex: '#E8CBA4', origin: 'Canada', grain: 'cream-pale with fine subtle streaks', story: 'Cream-pale with subtle streaks. Brightens rooms.', cncCompat: 'excellent' },
  { id: 'american-cherry', name: 'American Cherry', hex: '#A05A3F', origin: 'United States', grain: 'smooth pinkish-brown that deepens with age', story: 'Pinkish-brown that darkens dramatically over 2-3 years.', cncCompat: 'excellent' },
];

// Programmatic HD wood-grain SVG swatch per species — used for gallery cards
export const veneerGrainSvg = (species) => {
  // Slightly randomized-looking but deterministic per species
  const s = VENEER_SPECIES.find((v) => v.id === species) || VENEER_SPECIES[0];
  const seed = s.id.length + s.name.length;
  const lines = [];
  for (let i = 0; i < 22; i++) {
    const y = 8 + i * 6.2 + ((seed + i) % 3) * 0.8;
    const amp = 1.5 + ((i + seed) % 4) * 0.4;
    const wobble = 5 + ((i * 7 + seed) % 5);
    lines.push(
      <path key={i} d={`M0 ${y} Q ${30} ${y - amp} ${60} ${y + amp * 0.5} T 120 ${y - amp * 0.3} T 180 ${y + amp}`}
        stroke="rgba(0,0,0,0.18)" strokeWidth={wobble > 8 ? 0.7 : 0.4} fill="none" />
    );
  }
  // knots
  const knots = [
    { cx: 45, cy: 42, r: 4 },
    { cx: 135, cy: 88, r: 3 },
    { cx: 90, cy: 132, r: 3.5 },
  ];
  return (
    <svg viewBox="0 0 180 160" preserveAspectRatio="none" className="w-full h-full">
      <defs>
        <linearGradient id={`grad-${s.id}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={s.hex} stopOpacity="1" />
          <stop offset="100%" stopColor={s.hex} stopOpacity="0.75" />
        </linearGradient>
      </defs>
      <rect width="180" height="160" fill={`url(#grad-${s.id})`} />
      {lines}
      {knots.map((k, i) => (
        <ellipse key={i} cx={k.cx} cy={k.cy} rx={k.r} ry={k.r * 0.5} fill="rgba(0,0,0,0.22)" />
      ))}
      <rect width="180" height="160" fill="rgba(255,255,255,0.02)" />
    </svg>
  );
};

// -------- Mica palette (client copy) --------
export const MICA_PALETTE = [
  { id: 'ivory-marble', name: 'Ivory Marble', hex: '#F1EBDD', family: 'neutral' },
  { id: 'carrara-white', name: 'Carrara White', hex: '#EEECE6', family: 'white' },
  { id: 'sandstone', name: 'Sandstone Beige', hex: '#D9C4A2', family: 'warm' },
  { id: 'greige-plaster', name: 'Greige Plaster', hex: '#B9AA95', family: 'neutral' },
  { id: 'cement-grey', name: 'Cement Grey', hex: '#8E8B85', family: 'cool' },
  { id: 'charcoal-oak', name: 'Charcoal Oak', hex: '#4A4A47', family: 'dark' },
  { id: 'teakwood-classic', name: 'Teakwood Classic', hex: '#A26E44', family: 'wood' },
  { id: 'rosewood-brown', name: 'Rosewood Brown', hex: '#7B4B34', family: 'wood' },
  { id: 'walnut-noir', name: 'Walnut Noir', hex: '#5A3D2E', family: 'wood' },
  { id: 'smoked-oak', name: 'Smoked Oak', hex: '#8E6B4C', family: 'wood' },
  { id: 'birch-natural', name: 'Birch Natural', hex: '#D9BE94', family: 'wood' },
  { id: 'moss-green', name: 'Moss Green', hex: '#4E5E42', family: 'colour' },
  { id: 'ocean-teal', name: 'Ocean Teal', hex: '#2E5A66', family: 'colour' },
  { id: 'navy-midnight', name: 'Navy Midnight', hex: '#1E2B4A', family: 'colour' },
  { id: 'burgundy-wine', name: 'Burgundy Wine', hex: '#5C1E2B', family: 'colour' },
  { id: 'champagne-mist', name: 'Champagne Mist', hex: '#D6C6A1', family: 'warm' },
  { id: 'onyx-black', name: 'Onyx Black', hex: '#1B1B1B', family: 'dark' },
  { id: 'pearl-mist', name: 'Pearl Mist', hex: '#E9E4DC', family: 'white' },
  { id: 'concrete-storm', name: 'Concrete Storm', hex: '#6E6E68', family: 'cool' },
  { id: 'burnt-copper', name: 'Burnt Copper', hex: '#994F32', family: 'warm' },
];

// -------- PU Paint palette (24 colors) --------
export const PU_PAINT_PALETTE = [
  { id: 'pearl-white', name: 'Pearl White', hex: '#F5F3EE' },
  { id: 'ivory', name: 'Antique Ivory', hex: '#E8DFC7' },
  { id: 'champagne-gold', name: 'Champagne Gold', hex: '#D6BE7A' },
  { id: 'mist-grey', name: 'Mist Grey', hex: '#B7B2A9' },
  { id: 'storm-grey', name: 'Storm Grey', hex: '#7A7770' },
  { id: 'charcoal', name: 'Charcoal', hex: '#3D3D3B' },
  { id: 'midnight-black', name: 'Midnight Black', hex: '#111111' },
  { id: 'olive-military', name: 'Olive Military', hex: '#5B603E' },
  { id: 'forest-green', name: 'Forest Green', hex: '#2F4A34' },
  { id: 'ocean-teal', name: 'Ocean Teal', hex: '#2F5966' },
  { id: 'denim-blue', name: 'Denim Blue', hex: '#3D5A78' },
  { id: 'navy-royal', name: 'Navy Royal', hex: '#1B2B45' },
  { id: 'aubergine', name: 'Aubergine', hex: '#4A2C4A' },
  { id: 'wine-burgundy', name: 'Wine Burgundy', hex: '#5E1C2B' },
  { id: 'terracotta', name: 'Terracotta', hex: '#A75A3A' },
  { id: 'saffron-mustard', name: 'Saffron Mustard', hex: '#C88B2A' },
  { id: 'coral-blush', name: 'Coral Blush', hex: '#D07C68' },
  { id: 'dusty-rose', name: 'Dusty Rose', hex: '#B58784' },
  { id: 'sage', name: 'Sage', hex: '#95A38A' },
  { id: 'sky-blue', name: 'Sky Blue', hex: '#8AA6BE' },
  { id: 'peach-nude', name: 'Peach Nude', hex: '#E9C5A6' },
  { id: 'mocha-brown', name: 'Mocha Brown', hex: '#6B4A34' },
  { id: 'brass-antique', name: 'Antique Brass', hex: '#9C7B3F' },
  { id: 'rust-orange', name: 'Rust Orange', hex: '#B85E28' },
];

export const PU_SHEENS = [
  { id: 'matte', label: 'Matte', desc: 'Flat non-reflective sophistication' },
  { id: 'satin', label: 'Satin', desc: 'Soft sheen, timeless' },
  { id: 'gloss', label: 'High Gloss', desc: 'Automotive mirror finish' },
];

export const MATERIAL_CARE = {
  'acrylic-laminate': [
    'Clean with micro-fiber cloth to preserve mirror gloss',
    'Use only mild soapy water — no abrasives or ammonia',
    'Wipe spills immediately to maintain anti-fingerprint layer',
  ],
  'mica': [
    'Wipe daily with a damp soft cloth',
    'Avoid abrasive scrubbers or solvent cleaners',
    'For stubborn marks, use mild dish-soap solution',
  ],
  'veneer': [
    'Wipe with a soft dry cloth only — never wet',
    'Apply CriLam-recommended beeswax polish every 6 months',
    'Keep out of direct sunlight to preserve grain colour',
  ],
  'pu-paint': [
    'Clean with soft cloth + gentle car-shampoo solution',
    'Apply automotive-grade wax annually to preserve sheen',
    'Avoid solvents, thinners, and alcohol-based cleaners',
  ],
};

// Room × Material compatibility guidance (for smart warnings)
export const ROOM_MATERIAL_ADVICE = {
  'bathroom': {
    'veneer': { level: 'warn', msg: 'Veneer isn\'t ideal for wet zones — moisture can lift the wood grain over time. Try Acrylic Laminate instead.' },
    'pu-paint': { level: 'info', msg: 'PU Paint works in bathrooms but Acrylic Laminate is the safest 100% waterproof pick.' },
    'acrylic-laminate': { level: 'ok', msg: '✓ Perfect for wet zones — 100% waterproof, zero swelling.' },
    'mica': { level: 'ok', msg: '✓ Water-resistant when edge-sealed. Excellent for family bathrooms.' },
  },
  'kitchen': {
    'veneer': { level: 'warn', msg: 'Veneer can absorb cooking oils over time. Consider Acrylic Laminate or PU Paint for kitchen doors.' },
    'acrylic-laminate': { level: 'ok', msg: '✓ Heat, oil and stain resistant — ideal for kitchen doors.' },
    'mica': { level: 'ok', msg: '✓ Easy-clean daily surface, great value for kitchen.' },
    'pu-paint': { level: 'ok', msg: '✓ Automotive finish — resists oil splatter beautifully.' },
  },
  'main-entrance': {
    'veneer': { level: 'ok', msg: '✓ Classic statement — real wood warmth for a grand entrance.' },
    'pu-paint': { level: 'ok', msg: '✓ Custom-colour statement — 320+ Indian homes chose PU Paint main doors last month.' },
    'acrylic-laminate': { level: 'info', msg: 'Modern high-gloss main doors are trending — consider a signature dark tone.' },
    'mica': { level: 'info', msg: 'Mica works for main doors but Veneer/PU Paint have more statement gravitas.' },
  },
  'pooja': {
    'veneer': { level: 'ok', msg: '✓ Traditional warm-wood ambience — perfect for sacred spaces.' },
    'pu-paint': { level: 'ok', msg: '✓ Deep spiritual colours (maroon, gold, saffron) come alive in PU Paint.' },
    'acrylic-laminate': { level: 'ok', msg: '✓ Clean and modern spiritual space.' },
    'mica': { level: 'ok', msg: '✓ Rich warm shades pair with brass detailing.' },
  },
  'bedroom': {
    'veneer': { level: 'ok', msg: '✓ Warm, calming, timeless.' },
    'mica': { level: 'ok', msg: '✓ Cost-effective with huge shade variety.' },
    'acrylic-laminate': { level: 'ok', msg: '✓ Sleek modern bedroom aesthetic.' },
    'pu-paint': { level: 'ok', msg: '✓ Custom-colour statement bedroom.' },
  },
  'utility': {
    'acrylic-laminate': { level: 'ok', msg: '✓ Durable and easy-clean for utility.' },
    'mica': { level: 'ok', msg: '✓ Practical and budget-friendly.' },
    'veneer': { level: 'warn', msg: 'Veneer isn\'t ideal for utility (humidity/moisture).' },
    'pu-paint': { level: 'info', msg: 'PU works but Acrylic/Mica are more practical here.' },
  },
};

// CNC pattern → decorative SVG signature (used for gallery thumbnails)
// Kept intentionally minimal — a schematic hint of the pattern until AI-rendered.
export const cncSvgFor = (patternId) => {
  const styles = {
    'geo-vertical-lines': (
      <g stroke="currentColor" strokeWidth="0.8" fill="none">
        {[15, 30, 45, 60, 75, 90, 105].map((x) => <line key={x} x1={x} y1="10" x2={x} y2="180" />)}
      </g>
    ),
    'geo-diamond': (
      <g stroke="currentColor" strokeWidth="0.8" fill="none">
        {[30, 70, 110].map((x) => [30, 70, 110, 150].map((y) => (
          <polygon key={`${x}-${y}`} points={`${x},${y-15} ${x+15},${y} ${x},${y+15} ${x-15},${y}`} />
        )))}
      </g>
    ),
    'geo-horizontal-slats': (
      <g stroke="currentColor" strokeWidth="0.8" fill="none">
        {[25, 45, 65, 85, 105, 125, 145, 165].map((y) => <line key={y} x1="10" y1={y} x2="110" y2={y} />)}
      </g>
    ),
    'geo-chevron': (
      <g stroke="currentColor" strokeWidth="0.8" fill="none">
        {[30, 60, 90, 120, 150].map((y) => <polyline key={y} points={`15,${y+8} 60,${y-8} 105,${y+8}`} />)}
      </g>
    ),
    'geo-cross-hatch': (
      <g stroke="currentColor" strokeWidth="0.5" fill="none">
        {[20, 40, 60, 80, 100].map((v) => <line key={`h${v}`} x1="10" y1={v * 1.6} x2="110" y2={v * 1.6} />)}
        {[20, 40, 60, 80, 100].map((v) => <line key={`v${v}`} x1={v} y1="10" x2={v} y2="180" />)}
      </g>
    ),
    'geo-radial-lines': (
      <g stroke="currentColor" strokeWidth="0.8" fill="none">
        {Array.from({ length: 16 }).map((_, i) => {
          const a = (Math.PI * i) / 8;
          return <line key={i} x1="60" y1="95" x2={60 + Math.cos(a) * 65} y2={95 + Math.sin(a) * 80} />;
        })}
      </g>
    ),
    'trad-mandala': (
      <g stroke="currentColor" strokeWidth="0.7" fill="none">
        {[15, 30, 45, 60].map((r) => <circle key={r} cx="60" cy="95" r={r} />)}
        {Array.from({ length: 12 }).map((_, i) => {
          const a = (Math.PI * 2 * i) / 12;
          return <line key={i} x1="60" y1="95" x2={60 + Math.cos(a) * 60} y2={95 + Math.sin(a) * 60} />;
        })}
      </g>
    ),
    'trad-lotus': (
      <g stroke="currentColor" strokeWidth="0.8" fill="none">
        {Array.from({ length: 8 }).map((_, i) => {
          const a = (Math.PI * 2 * i) / 8;
          return <ellipse key={i} cx={60 + Math.cos(a) * 22} cy={95 + Math.sin(a) * 22} rx="12" ry="24" transform={`rotate(${(i * 45)} ${60 + Math.cos(a) * 22} ${95 + Math.sin(a) * 22})`} />;
        })}
        <circle cx="60" cy="95" r="8" />
      </g>
    ),
    'trad-peacock': (
      <g stroke="currentColor" strokeWidth="0.7" fill="none">
        {Array.from({ length: 7 }).map((_, i) => (
          <ellipse key={i} cx={60} cy={40 + i * 20} rx={22 + i * 3} ry="12" />
        ))}
      </g>
    ),
    'trad-om-symbol': (
      <g stroke="currentColor" strokeWidth="1.2" fill="none">
        <text x="60" y="105" textAnchor="middle" fontSize="70" fill="currentColor" opacity="0.85">ॐ</text>
        <rect x="15" y="15" width="90" height="160" rx="4" strokeDasharray="4 3" />
      </g>
    ),
    'trad-kalash': (
      <g stroke="currentColor" strokeWidth="0.8" fill="none">
        {[30, 60, 90].map((x) => (
          <g key={x}>
            <path d={`M${x-8},40 Q${x},20 ${x+8},40 Q${x+12},50 ${x-12},50 Z`} />
            <line x1={x} y1="20" x2={x} y2="10" />
          </g>
        ))}
        <path d="M15,60 Q60,75 105,60" />
        <path d="M15,160 Q60,175 105,160" />
      </g>
    ),
    'trad-paisley': (
      <g stroke="currentColor" strokeWidth="0.8" fill="none">
        {[40, 100, 160].map((y) => (
          <path key={y} d={`M25,${y} Q45,${y-25} 60,${y-10} Q80,${y+10} 95,${y}`} />
        ))}
      </g>
    ),
    'trad-ganesha-silhouette': (
      <g stroke="currentColor" strokeWidth="0.8" fill="none">
        <ellipse cx="60" cy="70" rx="20" ry="24" />
        <path d="M45,60 Q40,45 55,42" />
        <path d="M75,60 Q80,45 65,42" />
        <ellipse cx="60" cy="120" rx="30" ry="20" />
        <rect x="15" y="15" width="90" height="160" rx="4" strokeDasharray="3 3" />
      </g>
    ),
    'trad-mango-arch': (
      <g stroke="currentColor" strokeWidth="0.8" fill="none">
        <path d="M15,50 Q60,15 105,50" />
        {[30, 45, 60, 75, 90].map((x) => (
          <path key={x} d={`M${x-5},48 Q${x},38 ${x+5},48 Q${x},52 ${x-5},48`} />
        ))}
      </g>
    ),
    'min-single-groove': (
      <g stroke="currentColor" strokeWidth="1.5" fill="none">
        <line x1="60" y1="15" x2="60" y2="175" />
      </g>
    ),
    'min-dual-tone': (
      <g stroke="currentColor" strokeWidth="1.2" fill="none">
        <line x1="80" y1="15" x2="80" y2="175" />
        <rect x="15" y="15" width="65" height="160" fillOpacity="0.05" fill="currentColor" />
      </g>
    ),
    'min-triple-line': (
      <g stroke="currentColor" strokeWidth="0.8" fill="none">
        <line x1="15" y1="45" x2="105" y2="45" />
        <line x1="15" y1="95" x2="105" y2="95" />
        <line x1="15" y1="145" x2="105" y2="145" />
      </g>
    ),
    'min-l-groove': (
      <g stroke="currentColor" strokeWidth="0.8" fill="none">
        <polyline points="25,15 25,175 105,175" />
      </g>
    ),
    'min-japandi': (
      <g stroke="currentColor" strokeWidth="0.6" fill="none">
        <rect x="20" y="20" width="80" height="45" />
        <rect x="20" y="75" width="80" height="45" />
        <rect x="20" y="130" width="80" height="40" />
      </g>
    ),
    'panel-3-panel': (
      <g stroke="currentColor" strokeWidth="1" fill="none">
        <rect x="20" y="20" width="80" height="45" />
        <rect x="20" y="75" width="80" height="45" />
        <rect x="20" y="130" width="80" height="40" />
      </g>
    ),
    'panel-4-panel': (
      <g stroke="currentColor" strokeWidth="1" fill="none">
        <rect x="20" y="20" width="80" height="35" />
        <rect x="20" y="60" width="80" height="35" />
        <rect x="20" y="100" width="80" height="35" />
        <rect x="20" y="140" width="80" height="30" />
      </g>
    ),
    'panel-6-panel': (
      <g stroke="currentColor" strokeWidth="1" fill="none">
        {[0, 1, 2].map((row) => [0, 1].map((col) => (
          <rect key={`${row}-${col}`} x={20 + col * 42} y={20 + row * 52} width="38" height="45" />
        )))}
      </g>
    ),
    'panel-shaker': (
      <g stroke="currentColor" strokeWidth="1.2" fill="none">
        <rect x="18" y="18" width="84" height="164" />
        <rect x="30" y="30" width="60" height="140" />
      </g>
    ),
    'panel-cathedral': (
      <g stroke="currentColor" strokeWidth="1" fill="none">
        <path d="M20,55 Q60,15 100,55 L100,175 L20,175 Z" />
        <line x1="60" y1="20" x2="60" y2="175" />
      </g>
    ),
    'spec-bathroom-fluted': (
      <g stroke="currentColor" strokeWidth="0.7" fill="none">
        {[20, 30, 40, 50, 60, 70, 80, 90, 100].map((x) => <line key={x} x1={x} y1="10" x2={x} y2="180" />)}
      </g>
    ),
    'spec-main-brass-inset': (
      <g stroke="currentColor" strokeWidth="0.8" fill="none">
        <rect x="15" y="15" width="90" height="160" />
        <rect x="25" y="25" width="70" height="140" />
        <circle cx="60" cy="95" r="20" />
        <circle cx="60" cy="95" r="10" fillOpacity="0.15" fill="currentColor" />
      </g>
    ),
    'spec-pooja-tulsi-border': (
      <g stroke="currentColor" strokeWidth="0.8" fill="none">
        <rect x="18" y="18" width="84" height="164" strokeDasharray="3 2" />
        <text x="60" y="105" textAnchor="middle" fontSize="45" fill="currentColor" opacity="0.85">卐</text>
      </g>
    ),
    'spec-double-arched': (
      <g stroke="currentColor" strokeWidth="0.9" fill="none">
        <path d="M15,45 Q35,20 55,45 L55,175 L15,175 Z" />
        <path d="M65,45 Q85,20 105,45 L105,175 L65,175 Z" />
        <circle cx="35" cy="95" r="10" />
        <circle cx="85" cy="95" r="10" />
      </g>
    ),
  };
  return styles[patternId] || (
    <g stroke="currentColor" strokeWidth="0.8" fill="none">
      <rect x="18" y="18" width="84" height="164" />
    </g>
  );
};

// ------- PDF Design Sheet with FULL CriLam branding -------
// Uses jsPDF to build a spec-sheet PDF the customer can share with a dealer.
export async function generateBrandedDesignPDF({
  imageDataUrl,
  title,
  productLabel,
  designCode,
  finishLabel,
  finishCode,
  textureLabel,
  extraSpecs = [],
  logoUrl = '/crilam-logo-oval.png',
  customerName = null,
}) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  // Gold header bar
  doc.setFillColor(0, 69, 52); // CriLam green
  doc.rect(0, 0, pageW, 90, 'F');
  doc.setFillColor(212, 175, 55); // CriLam gold
  doc.rect(0, 90, pageW, 4, 'F');

  // Logo (top-left)
  try {
    const logoImg = await fetch(logoUrl).then((r) => r.blob());
    const logoDataUrl = await new Promise((res) => {
      const reader = new FileReader();
      reader.onloadend = () => res(reader.result);
      reader.readAsDataURL(logoImg);
    });
    doc.addImage(logoDataUrl, 'PNG', 30, 15, 60, 60);
  } catch {
    // silent — fall back to text-only header
  }

  // Header text
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.text('CriLam Decorative Pvt. Ltd.', 110, 40);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(212, 175, 55);
  doc.text('Premium 6H Acrylic Boards · CNC Doors · CriLam PUR-Bond™ Technology', 110, 58);
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(9);
  doc.text('123/628 (E) Domill Street, Fazalganj, Kanpur - 208012, UP  ·  +91 8298297475', 110, 75);

  // Title
  doc.setTextColor(0, 69, 52);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text(title || 'Your CriLam Design Sheet', 30, 130);

  // Sub-heading
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(80, 80, 80);
  const dateStr = new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' });
  doc.text(`Prepared for: ${customerName || 'CriLam Customer'}  ·  ${dateStr}`, 30, 150);

  // Design image
  const imgX = 30;
  const imgY = 170;
  const imgW = pageW - 60;
  const imgH = 320;
  try {
    doc.addImage(imageDataUrl, 'PNG', imgX, imgY, imgW, imgH, undefined, 'FAST');
  } catch {
    doc.setFillColor(230, 230, 230);
    doc.rect(imgX, imgY, imgW, imgH, 'F');
    doc.setTextColor(150, 150, 150);
    doc.text('Design preview unavailable', pageW / 2, imgY + imgH / 2, { align: 'center' });
  }

  // Spec panel
  const specY = imgY + imgH + 20;
  doc.setFillColor(247, 248, 245);
  doc.rect(30, specY, pageW - 60, 140, 'F');
  doc.setDrawColor(212, 175, 55);
  doc.setLineWidth(2);
  doc.line(30, specY, 30, specY + 140);

  doc.setTextColor(0, 69, 52);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text('Product Specifications', 45, specY + 22);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(60, 60, 60);
  const specs = [
    ['Product:', productLabel || '—'],
    ['Design Code:', designCode || '—'],
    ['Finish:', `${finishLabel || '—'}${finishCode ? ` · ${finishCode}` : ''}`],
    ['Texture:', textureLabel || '—'],
    ...extraSpecs,
  ];
  let sy = specY + 42;
  specs.forEach(([k, v]) => {
    doc.setFont('helvetica', 'bold');
    doc.text(k, 45, sy);
    doc.setFont('helvetica', 'normal');
    doc.text(String(v || '—'), 145, sy);
    sy += 15;
  });

  // Feature badges
  const badgesY = specY + 160;
  doc.setFillColor(0, 69, 52);
  doc.rect(30, badgesY, pageW - 60, 60, 'F');
  doc.setTextColor(212, 175, 55);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('CriLam PUR-Bond™ Technology Guarantees', 45, badgesY + 20);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text('•  6H Surface Hardness      •  Heat Resistant up to 150°C      •  100% Waterproof', 45, badgesY + 36);
  doc.text('•  Zero Bubbles · Zero Edge Lift      •  10-Year Written Guarantee      •  Nail-Free CNC', 45, badgesY + 50);

  // Footer
  doc.setTextColor(120, 120, 120);
  doc.setFontSize(8);
  doc.text(
    'This design sheet was generated by the CriLam AI Design Studio.  For an exact quote, contact your nearest CriLam dealer or call +91 8298297475.',
    pageW / 2, pageH - 30, { align: 'center' }
  );
  doc.setTextColor(212, 175, 55);
  doc.setFont('helvetica', 'bold');
  doc.text('www.crilamdecorative.com  ·  info@crilamdecorative.com', pageW / 2, pageH - 18, { align: 'center' });

  return doc;
}
